import { Dir, FPS, PHYSICS_SCALE, Point, TILE_SIZE } from "../constants";
import { Controller } from "../controller/controller";
import { Level, Tile } from "../level";

const MAX_MOVEMENT_PER_STEP = Math.round(0.4 * TILE_SIZE);

let entityCounter = 0;

export enum FacingDir {
    LEFT,
    RIGHT,
}

export class Entity {
    level: Level;

    x: number = 0;
    y: number = 0;
    w: number = 0;
    h: number = 0;
    dx: number = 0;
    dy: number = 0;
    canColide = true;
    // This is what the other one did, who knows what works for this game.
    gravity = (1 / 8) * PHYSICS_SCALE * FPS * FPS;
    xDampAmt = (1 / 8) * PHYSICS_SCALE * FPS * FPS;
    facingDir = FacingDir.RIGHT;
    running = false;
    done = false;

    index = entityCounter++;

    animCount: number = 0;

    controller?: Controller;

    debugColor? = "#f68187";

    constructor(level: Level) {
        this.level = level;
    }

    update(dt: number) {
        this.animCount += dt;

        this.updateController(dt);

        this.applyGravity(dt);
        this.moveX(this.dx * dt);
        this.moveY(this.dy * dt);
    }

    updateController(dt:number) {
        this.controller?.update(this, dt);
    }

    render(context: CanvasRenderingContext2D) {
        if (this.debugColor == null) {
            return;
        }
        context.fillStyle = this.debugColor;
        context.fillRect(this.x, this.y, this.w, this.h);
    }

    applyGravity(dt: number) {
        this.dy += this.gravity * dt;
    }

    // Stuff for controllers to use:
    moveLeft(dt: number) {}

    moveRight(dt: number) {}

    jump() {}

    dampenX(dt: number) {
        if (this.dx > this.xDampAmt * dt) {
            this.dx -= this.xDampAmt * dt;
        } else if (this.dx < -this.xDampAmt * dt) {
            this.dx += this.xDampAmt * dt;
        } else {
            this.dx = 0;
        }
    }

    moveX(dx: number): boolean {
        dx = Math.round(dx);

        while (dx > MAX_MOVEMENT_PER_STEP) {
            this.moveX(MAX_MOVEMENT_PER_STEP);
            dx -= MAX_MOVEMENT_PER_STEP;
        }
        while (dx < -MAX_MOVEMENT_PER_STEP) {
            this.moveX(-MAX_MOVEMENT_PER_STEP);
            dx += MAX_MOVEMENT_PER_STEP;
        }

        this.x += dx;

        if (!this.canColide) {
            return false;
        }

        if (dx < 0) {
            // Moving left
            if (this.isTouching(Tile.GROUND, { dir: Dir.LEFT })) {
                this.onLeftCollision();
                return true;
            }
        } else if (dx > 0) {
            // Moving right
            if (this.isTouching(Tile.GROUND, { dir: Dir.RIGHT })) {
                this.onRightCollision();
                return true;
            }
        }
        return false;
    }

    moveY(dy: number): boolean {
        while (dy > MAX_MOVEMENT_PER_STEP) {
            this.moveY(MAX_MOVEMENT_PER_STEP);
            dy -= MAX_MOVEMENT_PER_STEP;
        }
        while (dy < -MAX_MOVEMENT_PER_STEP) {
            this.moveY(-MAX_MOVEMENT_PER_STEP);
            dy += MAX_MOVEMENT_PER_STEP;
        }

        this.y = Math.round(this.y + dy);

        if (!this.canColide) {
            return false;
        }

        if (dy < 0) {
            if (this.isTouching(Tile.GROUND, { dir: Dir.UP })) {
                this.onUpCollision();
                return true;
            }
        } else if (dy > 0) {
            if (this.isTouching(Tile.GROUND, { dir: Dir.DOWN })) {
                this.onDownCollision();
                return true;
            }
        }
        return false;
    }

    // Functions that can also be edited by subclasses
    onUpCollision() {
        const resetPos = this.level.getTilePosFromCoord(
            { y: this.minY },
            { y: 1 }
        );
        this.minY = resetPos + 1;

        this.dy = 0;
    }
    onDownCollision() {
        const resetPos = this.level.getTilePosFromCoord(
            { y: this.maxY },
            { y: 0 }
        );
        this.maxY = resetPos - 1;

        this.dy = 0;
    }
    onLeftCollision() {
        const resetPos = this.level.getTilePosFromCoord(
            { x: this.minX },
            { x: 1 }
        );
        this.minX = resetPos + 1;

        this.dx = 0;
    }
    onRightCollision() {
        const resetPos = this.level.getTilePosFromCoord(
            { x: this.maxX },
            { x: 0 }
        );
        this.maxX = resetPos - 1;

        this.dx = 0;
    }

    isStandingOnGround() {
        return this.isTouching(Tile.GROUND, {
            dir: Dir.DOWN,
            offset: { x: 0, y: 1 },
        });
    }

    isTouching(
        tile: Tile,
        { dir, offset = { x: 0, y: 0 } }: { dir?: Dir; offset?: Point } = {}
    ) {
        let coords: Point[];
        switch (dir) {
            case Dir.LEFT:
                coords = [
                    { x: this.minX, y: this.minY },
                    { x: this.minX, y: this.maxY },
                ];
                break;
            case Dir.RIGHT:
                coords = [
                    { x: this.maxX, y: this.minY },
                    { x: this.maxX, y: this.maxY },
                ];
                break;
            case Dir.UP:
                coords = [
                    { x: this.minX, y: this.minY },
                    { x: this.maxX, y: this.minY },
                ];
                break;
            case Dir.DOWN:
                coords = [
                    { x: this.minX, y: this.maxY },
                    { x: this.maxX, y: this.maxY },
                ];
                break;
            default:
                coords = [
                    { x: this.minX, y: this.minY },
                    { x: this.maxX, y: this.minY },
                    { x: this.minX, y: this.maxY },
                    { x: this.maxX, y: this.maxY },
                ];
        }
        return (
            coords
                // Add the offset
                .map((coord) => {
                    coord.x += offset.x;
                    coord.y += offset.y;
                    return coord;
                })
                // check if any position is touching
                .some((coord) => this.level.getTileFromCoord(coord) == tile)
        );
    }

    isOn(tile: Tile) {
        const coords = [
            { x: this.minX, y: this.minY },
            { x: this.maxX, y: this.minY },
            { x: this.minX, y: this.maxY },
            { x: this.maxX, y: this.maxY },
        ];
        return coords.every(
            (coord) => this.level.getTileFromCoord(coord) == tile
        );
    }

    isTouchingEntity(ent: Entity, leniency: number = 0) {
        return (
            ent.maxX + leniency > this.minX &&
            this.maxX + leniency > ent.minX &&
            ent.maxY + leniency > this.minY &&
            this.maxY + leniency > ent.minY
        );
    }

    //#region Getters and setter and junk.
    get minX() {
        return this.x;
    }

    set minX(val: number) {
        this.x = val;
    }

    get midX() {
        return this.x + this.w / 2;
    }

    set midX(val: number) {
        this.x = val - this.w / 2;
    }

    get maxX() {
        return this.x + this.w;
    }

    set maxX(val: number) {
        this.x = val - this.w;
    }

    get minY() {
        return this.y;
    }

    set minY(val: number) {
        this.y = val;
    }

    get midY() {
        return this.y + this.h / 2;
    }

    set midY(val: number) {
        this.y = val - this.h / 2;
    }

    get maxY() {
        return this.y + this.h;
    }

    set maxY(val: number) {
        this.y = val - this.h;
    }

    //#endregion
}
